/**
 * Staff portal: My Profile (/app/members/my-profile).
 *
 * The shared MemberProfile in self mode (api/members/me/*: personal, contact, addresses, health,
 * family, campus, emergency contacts) plus two HR tabs when the user has an employee record:
 * Employment (job title and employee number are editable) and Files.
 *
 * Works for everyone: a user without an HR employee record just sees the member profile, and a
 * user without a member record is told the church office links it (PUT /me cannot create one).
 * The `?tb=` query parameter keeps its old aliases (overview, member, allergies, documents, ...).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconBriefcase, IconFile, IconEdit, IconInfoCircle, IconUserCheck, IconUserMinus, IconClockHour4, IconLink, IconCalendarTime, IconAlertCircle,
} from '@tabler/icons-react';
import {
  Container, Stack, Group, Card, Badge, Button, Text, SimpleGrid, Alert, Tooltip, ThemeIcon, Modal, TextInput, Divider,
} from '@/components/ui';
import { FadeIn } from '@/components/animations';
import { PageHelpButton } from '@/components/help';
import { toast } from '@/components/ui/providers/AlertProvider';
import { useStaffPortalProfileService } from '@actschurch/ccms-sdk';
import { MemberProfile, errorMessage, errorStatus, type MemberProfileExtraTab } from '@/components/members';
import { hrKeys } from '../utils/queryKeys';
import { formatDate } from '../utils/hrHelpers';
import { MyFilesPanel } from './components/MyFilesPanel';

const TAB_ALIAS: Record<string, string> = {
  overview: 'personal',
  info: 'personal',
  member: 'personal',
  membership: 'personal',
  hr: 'employment',
  file: 'files',
  documents: 'files',
  allergies: 'health',
  disabilities: 'health',
  address: 'addresses',
  family: 'family',
  household: 'family',
  contacts: 'emergency',
};

function tint(color?: string | null) {
  return color ? { backgroundColor: `${color}22`, color } : undefined;
}

function Row({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <Group justify="space-between" wrap="nowrap">
      <Text size="sm" c="dimmed">{label}</Text>
      <Text size="sm" ta="right">{value === null || value === undefined || value === '' ? 'Not set' : value}</Text>
    </Group>
  );
}

export default function MyProfilePage() {
  const profileService = useStaffPortalProfileService();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ jobTitle: '', employeeNo: '' });

  // HR employee record: optional. Non-staff users have none and that is fine.
  const hr = useQuery({
    queryKey: hrKeys.myProfile(),
    queryFn: () => profileService.getProfile(),
    retry: false,
  });
  const profile = hr.data ?? null;
  const noHrRecord = hr.isError && (errorStatus(hr.error) === 404 || /not found/i.test(errorMessage(hr.error, '')));

  const saveHr = useMutation({
    mutationFn: () => profileService.updateProfile({
      jobTitle: form.jobTitle.trim() || undefined,
      employeeNo: form.employeeNo.trim() || undefined,
    }),
    onSuccess: () => {
      toast.success('Saved', 'Your employment details have been updated.');
      queryClient.invalidateQueries({ queryKey: hrKeys.myProfile() });
      setEditOpen(false);
    },
    onError: (e) => toast.error('Could not save', errorMessage(e)),
  });

  const openEdit = () => {
    setForm({ jobTitle: profile?.jobTitle ?? '', employeeNo: profile?.employeeNo ?? '' });
    saveHr.reset();
    setEditOpen(true);
  };

  const isUnenriched = !!profile && !profile.name && !profile.surname && !profile.email && !profile.fullName;

  const extraTabs: MemberProfileExtraTab[] = profile ? [
    {
      value: 'employment',
      label: 'Employment',
      icon: <IconBriefcase size={16} />,
      content: (
        <Stack gap="md">
          {isUnenriched && (
            <Alert icon={<IconInfoCircle size={16} />} color="yellow" alertVariant="light">
              Your staff details have not synced yet. Names and email fill in automatically, usually within a minute.
            </Alert>
          )}
          <Group justify="flex-end">
            <Button variant="light" leftSection={<IconEdit size={16} />} onClick={openEdit} data-help-id="hr.my-profile.edit-employment">Edit employment details</Button>
          </Group>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md" data-help-id="hr.my-profile.employment">
            <Card withBorder padding="md" radius="md">
              <Group gap="sm" mb="xs">
                <ThemeIcon size="md" variant="light" color="blue"><IconBriefcase size={16} /></ThemeIcon>
                <Text size="sm" fw={600}>Employment record</Text>
              </Group>
              <Divider mb="xs" />
              <Stack gap={6}>
                <Row label="Employee number" value={profile.employeeNo} />
                <Row label="Job title" value={profile.jobTitle} />
                <Row label="Type" value={profile.typeName} />
                <Row label="Status" value={profile.statusName} />
              </Stack>
            </Card>
            <Card withBorder padding="md" radius="md">
              <Group gap="sm" mb="xs">
                <ThemeIcon size="md" variant="light" color="green"><IconCalendarTime size={16} /></ThemeIcon>
                <Text size="sm" fw={600}>Tenure</Text>
              </Group>
              <Divider mb="xs" />
              <Stack gap={6}>
                <Row label="Started" value={profile.startDate ? formatDate(profile.startDate) : null} />
                <Row label="Ends" value={profile.endDate ? formatDate(profile.endDate) : null} />
                <Row label="Years of service" value={profile.yearsOfService} />
                <Row label="Tenure level" value={profile.tenureLevel} />
              </Stack>
            </Card>
          </SimpleGrid>
        </Stack>
      ),
    },
    { value: 'files', label: 'Files', icon: <IconFile size={16} />, content: <MyFilesPanel compact helpIdPrefix="hr.my-profile" /> },
  ] : [];

  const extraBadges = profile ? (
    <>
      {profile.employeeNo && <Badge variant="light" color="gray">Staff #{profile.employeeNo}</Badge>}
      {profile.typeName && <Badge variant="light" style={tint(profile.typeColor)}>{profile.typeName}</Badge>}
      {profile.employmentStatus && (
        <Badge variant="light" style={tint(profile.employmentStatusColor)}
          leftSection={profile.isCurrentlyEmployed ? <IconUserCheck size={12} /> : <IconUserMinus size={12} />}>
          {profile.employmentStatus}
        </Badge>
      )}
      {profile.tenureLevel && (
        <Badge variant="light" style={tint(profile.tenureLevelColor)} leftSection={<IconClockHour4 size={12} />}>
          {profile.tenureLevel}{profile.yearsOfService != null ? ` · ${profile.yearsOfService} yr${profile.yearsOfService === 1 ? '' : 's'}` : ''}
        </Badge>
      )}
      {profile.isPaySpaceLinked && (
        <Tooltip label={`PaySpace ID: ${profile.paySpaceEmployeeId}`}>
          <Badge variant="light" color="grape" leftSection={<IconLink size={12} />}>PaySpace</Badge>
        </Tooltip>
      )}
    </>
  ) : null;

  return (
    <FadeIn>
      <Container size="xl" py="md">
        <Stack gap="md">
          {hr.isError && !noHrRecord && (
            <Alert color="yellow" icon={<IconAlertCircle size={16} />} title="Staff details could not be loaded">
              {errorMessage(hr.error)} Your member profile is shown below.
            </Alert>
          )}
          {noHrRecord && (
            <Alert color="blue" alertVariant="light" icon={<IconInfoCircle size={16} />}>
              You do not have a staff record, so only your member profile is shown. HR adds staff records.
            </Alert>
          )}

          <MemberProfile
            mode="self"
            tabParam="tb"
            tabAliases={TAB_ALIAS}
            extraTabs={extraTabs}
            extraBadges={extraBadges}
            helpPageKey="hr.my-profile"
            headerActions={<PageHelpButton pageKey="hr.my-profile" />}
          />
        </Stack>

        <Modal opened={editOpen} onClose={() => setEditOpen(false)} title="Edit employment details" size="md" centered>
          <Stack gap="md">
            <TextInput label="Employee number" placeholder="e.g. EMP-1042" value={form.employeeNo} onChange={(e) => setForm({ ...form, employeeNo: e.currentTarget.value })} />
            <TextInput label="Job title" placeholder="e.g. Youth Pastor" value={form.jobTitle} onChange={(e) => setForm({ ...form, jobTitle: e.currentTarget.value })} />
            {saveHr.isError && <Alert color="red" icon={<IconAlertCircle size={16} />}>{errorMessage(saveHr.error)}</Alert>}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button loading={saveHr.isPending} onClick={() => saveHr.mutate()}>Save</Button>
            </Group>
          </Stack>
        </Modal>
      </Container>
    </FadeIn>
  );
}
